export const EXPLORER_PREFIX = "explorer";
export const PRPV_PARAM = "prvpr";
export const GEOCATALOGNAME_PARAM = "geocatalogname";
export const PRPV_HOST_INDICATOR = "ppe.spatio";
export const PRPV_HOST_REPLACEMENT = "spatio-ppe";

// We expect the url of the current window to be of the form
// https://explorer.geocatalog.spatio.azure.com/?geocatalogname=<geocatalogname.namespace.region>

// From this we want to extract the base url which is of the form
// https://<geocatalogname>.<namespace>.<region>.geocatalog.spatio.azure.com/
// This base url will be used to make all the API calls from the application

const getBaseUrl = (currentUrl: string): string => {
  const url = new URL(currentUrl);

  // When running on localhost, derive all API base URLs from the current origin
  // so no ?geocatalogname=... query parameter is required in the URL.
  if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
    return url.origin;
  }

  let geocatalogName = "default";
  let namespace = "default";
  let region = "default";

  const nameParam = url.searchParams.get(GEOCATALOGNAME_PARAM) || "default";
  // Parse the geocatalogname, namespace, and region from the nameParam
  let prvPRState = url.searchParams.get(PRPV_PARAM) === "true" ? true : false;
  const nameParts = nameParam.split(".");
  if (nameParts.length === 3) {
    [geocatalogName, namespace, region] = nameParts;
  }
  let fragment = url.hash.substring(1);
  let params = new URLSearchParams(fragment);
  let state = params.get("state");
  if (state) {
    prvPRState = state.split("|")[2] === "true" ? true : false;
    state = state.split("|")[1];

    // state is of the form <geocatalogname>.<namespace>.<region>
    const stateParts = state.split(".");
    if (stateParts.length === 3) {
      const [stateGeocatalogName, stateNamespace, stateRegion] = stateParts;
      // Validate that all these parts are alphanumeric and hyphens/underscores only
      // and don't begin or end with a hyphen
      const validPart = (part: string) =>
        /^[a-zA-Z0-9](?:[a-zA-Z0-9-_]*[a-zA-Z0-9])?$/.test(part);
      if (
        validPart(stateGeocatalogName) &&
        validPart(stateNamespace) &&
        validPart(stateRegion)
      ) {
        geocatalogName = stateGeocatalogName;
        namespace = stateNamespace;
        region = stateRegion;
      }
    }
  }
  // if prvPRState is true, we want to change the base domain
  const baseDomain = prvPRState
    ? url.host
        .substring(url.host.indexOf(".") + 1)
        .replace(PRPV_HOST_REPLACEMENT, PRPV_HOST_INDICATOR)
    : url.host.substring(url.host.indexOf(".") + 1);

  return `https://${geocatalogName}.${namespace}.${region}.${baseDomain}`;
};

const baseUrl = getBaseUrl(window.location.href);
export const API_VERSION = process.env.REACT_APP_API_VERSION || "2025-04-30-preview";

export const API_ROOT_PATH =
  process.env.REACT_APP_API_ROOT_PATH || API_VERSION === "2024-01-31-preview"
    ? "api"
    : "";
export const STAC_URL = process.env.REACT_APP_STAC_ROOT || `${baseUrl}/stac`;
export const CONFIG_URL =
  process.env.REACT_APP_CONFIG_ROOT || `${baseUrl}/geocatalog`;
export const DATA_URL = process.env.REACT_APP_TILER_ROOT || `${baseUrl}/data`;
export const INMA_URL = process.env.REACT_APP_INMA_ROOT || `${baseUrl}/inma`;
export const IMAGE_URL = process.env.REACT_APP_IMAGE_API_ROOT || `${baseUrl}/data`;
export const REDIRECT_URL = process.env.REACT_APP_REDIRECT_URI || `${baseUrl}`;
export const AZURE_CLOUD_NAME =
  process.env.REACT_APP_AZURE_CLOUD_NAME || "AzurePublicCloud";

// Feature flag to enable/disable Explorer (Azure Maps) functionality
// We want to enable Explorer only for AzureUSGovernmentCloud a.k.a Fairfax
// and AzurePublicCloud which covers public, ppe and dogfood environments.
export const EXPLORER_ENABLED =
  AZURE_CLOUD_NAME === "AzureUSGovernmentCloud" ||
  AZURE_CLOUD_NAME === "AzurePublicCloud";
export const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
export const MILLISECONDS_PER_MINUTE = 60 * 1000;
interface CloudDomainConfig {
  atlasDomain: string;
  graphDomain: string;
}

const cloudDomainConfigs: Record<string, CloudDomainConfig> = {
  AzureUSGovernmentCloud: {
    atlasDomain: "atlas.azure.us",
    graphDomain: "graph.microsoft.us",
  },
  AzureChinaCloud: {
    atlasDomain: "atlas.azure.cn",
    graphDomain: "graph.chinacloudapi.cn",
  },
  AzureGermanCloud: {
    atlasDomain: "atlas.microsoft.de",
    graphDomain: "graph.microsoft.de",
  },
  AzurePublicCloud: {
    atlasDomain: "atlas.microsoft.com",
    graphDomain: "graph.microsoft.com",
  },
};

const defaultConfig: CloudDomainConfig = {
  atlasDomain: "atlas.microsoft.com",
  graphDomain: "graph.microsoft.com",
};

const currentConfig = cloudDomainConfigs[AZURE_CLOUD_NAME] || defaultConfig;

export const ATLAS_DOMAIN = currentConfig.atlasDomain;
export const GRAPH_DOMAIN = currentConfig.graphDomain;

export const X_REQUEST_ENTITY = "X-PC-Request-Entity";
export const QS_REQUEST_ENTITY = "request_entity";
export const REQUEST_ENTITY = "explorer";

export const MOBILE_WIDTH = 430;

export const STAC_COLLECTION_SCHEMA_URI =
  "https://raw.githubusercontent.com/radiantearth/stac-spec/master/collection-spec/json-schema/collection.json";

export const GRAPH_PHOTO_URL = "https://" + GRAPH_DOMAIN + "/v1.0/me/photo/$value";

export const URLS = {
  home: "/",
  explorer: "/explorer",
  collections: "/collections",
  operations: "/operations",
  settings: "/settings",
  terms: "/terms",
  docs: "https://learn.microsoft.com/en-us/azure/planetary-computer/",
  json: "/json-api",
};
